import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { FaFileUpload } from "react-icons/fa";
import { IoMdCloseCircle } from "react-icons/io";
import { MdClose } from "react-icons/md";
import { RiEditFill } from "react-icons/ri";

// ID documents must be PDF (the file picker's accept only suggests it; any file can still be chosen)
const isPdfFile = (file) =>
  Boolean(file) && (file.type === "application/pdf" || /\.pdf$/i.test(file.name || ""));
const PDF_ONLY_MESSAGE = "Only PDF files are allowed.";

const VerifyInfoEdit = ({ profiledate, sellerId }) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const [save, setSave] = useState(false);

  const [governmentID, setGovernmentID] = useState(null);
  const [businessCertificate, setBusinessCertificate] = useState(null);
  const [fileErrors, setFileErrors] = useState({});
  // Reject non-PDF files as soon as they're chosen
  const rejectNonPdf = (field) => (e) => {
    const file = e.target.files?.[0];
    if (file && !isPdfFile(file)) {
      e.target.value = "";
      setValue(field, null);
      setFileErrors((prev) => ({ ...prev, [field]: PDF_ONLY_MESSAGE }));
    } else if (file) {
      setFileErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Preload server data into the form
  useEffect(() => {
    if (profiledate?.govt_id?.url) {
      setGovernmentID({
        name: profiledate.govt_id.url.split("/").pop(),
        url: profiledate.govt_id.url,
      });
      setValue("governmentID", profiledate.govt_id.url); // store url as value
    }
    if (profiledate?.business_certificate?.url) {
      setBusinessCertificate({
        name: profiledate.business_certificate.url.split("/").pop(),
        url: profiledate.business_certificate.url,
      });
      setValue("businessCertificate", profiledate.business_certificate.url);
    }
  }, [profiledate, setValue]);

  // Watch for new uploads
  const watchGovernmentID = watch("governmentID");
  const watchBusinessCertificate = watch("businessCertificate");

  useEffect(() => {
    if (watchGovernmentID instanceof FileList && watchGovernmentID.length > 0) {
      setGovernmentID(watchGovernmentID[0]);
    }
  }, [watchGovernmentID]);

  useEffect(() => {
    if (
      watchBusinessCertificate instanceof FileList &&
      watchBusinessCertificate.length > 0
    ) {
      setBusinessCertificate(watchBusinessCertificate[0]);
    }
  }, [watchBusinessCertificate]);

  const onSubmit = (data) => {
    console.log("Form submitted:", data);
  };

  return (
    <div className=" p-0  sm:p-5 space-y-8 ">
      <div className="flex flex-row justify-between border-b-2">
        <p className="text-lg text-[#095228] font-medium">
          Verify Your Identity
        </p>
        {!sellerId &&
          (save === true ? (
            <button
              onClick={() => setSave(false)}
              className="text-xs m-1 bg-green-800 py-1 rounded-xl px-3 font-medium text-white file:"
            >
              Close
            </button>
          ) : (
            <button onClick={() => setSave(true)} className="text-xs">
              <RiEditFill className="text-[18px]" />
            </button>
          ))}
      </div>

      <div>
        {save === true ? (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="p-0 rounded-lg space-y-4"
          >
            {/* Government ID */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold">
                Government - Issued ID
              </label>
              <div className="flex items-center gap-3">
                <div className="flex items-center w-full border rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-green-600">
                  <input
                    type="file"
                    accept="application/pdf"
                    id="gov-id-upload"
                    {...register("governmentID", { required: true, onChange: rejectNonPdf("governmentID") })}
                    className="hidden"
                  />

                  <label
                    htmlFor="gov-id-upload"
                    className="flex-grow cursor-pointer text-sm text-gray-500 truncate"
                  >
                    {governmentID
                      ? governmentID.name
                      : "Upload Government-issued ID"}
                  </label>

                  <label htmlFor="gov-id-upload" className="cursor-pointer">
                    <FaFileUpload className="text-[25px]" />
                  </label>
                </div>

                {governmentID && (
                  <button
                    type="button"
                    onClick={() => {
                      setGovernmentID(null);
                      setValue("governmentID", null);
                    }}
                    className="text-red-600 hover:text-red-800 text-lg"
                  >
                    <MdClose />
                  </button>
                )}
              </div>
              {fileErrors.governmentID && (
                <p className="text-sm text-red-500">{fileErrors.governmentID}</p>
              )}
              {errors.governmentID && (
                <p className="text-sm text-red-500">
                  Government ID is required
                </p>
              )}
            </div>

            {/* Business Certificate */}
            <div className="flex items-start gap-3">
              <div className="w-full">
                <label className="block text-sm font-semibold mb-1">
                  Business Certificate{" "}
                  <span className="text-xs text-gray-500 italic">
                    (for Business Seller)
                  </span>
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center w-full border rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-green-600">
                    <input
                      type="file"
                      accept="application/pdf"
                      id="business-cert-upload"
                      {...register("businessCertificate", { onChange: rejectNonPdf("businessCertificate") })}
                      className="hidden"
                    />
                    <label
                      htmlFor="business-cert-upload"
                      className="flex-grow cursor-pointer text-sm text-gray-500 truncate"
                    >
                      {businessCertificate
                        ? businessCertificate.name
                        : "Upload Business Certificate"}
                    </label>
                    <label
                      htmlFor="business-cert-upload"
                      className="cursor-pointer"
                    >
                      <FaFileUpload className="text-[25px]" />
                    </label>
                  </div>
                  {businessCertificate && (
                    <button
                      type="button"
                      onClick={() => {
                        setBusinessCertificate(null);
                        setValue("businessCertificate", null);
                      }}
                      className="text-red-600 hover:text-red-800 text-lg"
                    >
                      <MdClose />
                    </button>
                  )}
                </div>
                {fileErrors.businessCertificate && (
                  <p className="text-sm text-red-500">{fileErrors.businessCertificate}</p>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="mt-4 px-4 py-2 rounded-full bg-green-700 text-white hover:bg-green-800"
            >
              Save
            </button>
          </form>
        ) : (
          <div>
            {/* Readonly View */}
            <div>
              <span className="text-gray-500 font-medium">
                Government - Issued ID
              </span>
              <div className="flex items-center gap-2 mt-1">
                {profiledate?.govt_id?.url ? (
                  <a
                    href={profiledate.govt_id.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline text-sm"
                  >
                    {profiledate.govt_id.url.split("/").pop()}
                  </a>
                ) : (
                  "xxxxxxx"
                )}
              </div>
            </div>
            <div className="mt-5">
              <span className="text-gray-500 font-medium">
                Business Certificate (for Business Seller)
              </span>
              <div className="flex items-center gap-2 mt-1">
                {profiledate?.business_certificate?.url ? (
                  <a
                    href={profiledate.business_certificate.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline text-sm"
                  >
                    {profiledate.business_certificate.url.split("/").pop()}
                  </a>
                ) : (
                  "xxxxxxx"
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyInfoEdit;
